import { type ElementType } from 'react';
import { cn } from '@/utils/cn';
import { Icon } from '../Icon';
import { Typography } from '../Typography';
import { Badge, BadgeState } from '../Badge';

export type ProductSelectCardState = 'default' | 'selected' | 'outOfStock';

/** Sealed — no `className`; layout belongs on the parent. */
export interface ProductSelectCardProps {
  productName: string;
  brandName?: string;
  /** A placeholder is shown when omitted. */
  imageUrl?: string | null;
  /** Number of days shown in the `default` state. */
  badgeNumber?: number;
  /** `selected` → success border + check; `outOfStock` → dimmed image, disabled, "0 left". */
  state?: ProductSelectCardState;
  /** When provided, the card renders as an interactive `<button>`. */
  onClick?: () => void;
}

/** Selectable product card with default, selected, and out-of-stock states. */
export function ProductSelectCard({
  productName,
  brandName,
  imageUrl,
  badgeNumber,
  state = 'default',
  onClick,
}: ProductSelectCardProps) {
  const isSelected = state === 'selected';
  const isOutOfStock = state === 'outOfStock';
  const Root: ElementType = onClick ? 'button' : 'div';

  return (
    <Root
      {...(onClick
        ? { type: 'button' as const, onClick, disabled: isOutOfStock, 'aria-pressed': isSelected }
        : {})}
      className={cn(
        'w-full text-left rounded-medium border bg-surface overflow-hidden',
        'shadow-(--shadow-top-light-first-floor) transition-colors',
        isSelected ? 'border-success' : 'border-default',
        isOutOfStock && 'cursor-not-allowed'
      )}
    >
      <div className="relative flex flex-row items-stretch p-4 gap-3">
        <div className="w-2/5 aspect-square shrink-0">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={productName}
              className={cn(
                'w-full h-full object-cover object-center',
                isOutOfStock && 'opacity-25'
              )}
            />
          ) : (
            <div className="w-full h-full bg-disabled" aria-hidden="true" />
          )}
        </div>

        <div className="border-l border-default self-stretch" aria-hidden="true" />

        <div
          className={cn(
            'flex flex-col gap-1 justify-center flex-1 min-w-0',
            // Reserve room for the absolutely-positioned corner marker below so a
            // wrapped product name/brand can't grow into and overlap it.
            (isSelected || isOutOfStock) && 'pb-8'
          )}
        >
          {brandName && (
            <Typography variant="body" size="small" color="bodylight">
              {brandName}
            </Typography>
          )}
          <Typography variant="header" size="h3" as="h3">
            {productName}
          </Typography>
          {!isSelected &&
            (isOutOfStock ? (
              <div className="absolute right-4 bottom-4">
                <Badge state={BadgeState.OUT_OF_STOCK} />
              </div>
            ) : (
              badgeNumber !== undefined && (
                <div className="self-start">
                  <Badge state={BadgeState.DAYS_LEFT} number={badgeNumber} />
                </div>
              )
            ))}
        </div>

        {isSelected && (
          <div className="absolute right-4 bottom-4">
            <Icon name="circle-tick-filled" size="large" color="success" />
          </div>
        )}
      </div>
    </Root>
  );
}

export default ProductSelectCard;
